import type { Question } from "../types/question.types";

/** Ensure answer values are numbers (API may return strings after reload). */
export function normalizeAnswers(
  raw: Record<string, unknown> | null | undefined,
): Record<string, number> {
  if (!raw || typeof raw !== "object") return {};
  return Object.fromEntries(
    Object.entries(raw).map(([k, v]) => {
      const n = typeof v === "number" ? v : Number(v);
      return [k, Number.isFinite(n) ? n : 0];
    }),
  );
}

function getAnswerableQuestions(questions: Question[]): Question[] {
  return questions.filter((q) => q.layout !== "instructions");
}

/** Compute completion % 0–100 for API (answerable questions only). */
export function getProgressPercent(
  questions: Question[],
  answers: Record<string, number>,
): number {
  const answerable = getAnswerableQuestions(questions);
  const total = answerable.length;
  if (total === 0) return 0;
  const answered = answerable.filter((q) =>
    isQuestionAnswered(q, answers),
  ).length;
  return Math.round((answered / total) * 100);
}

export function isQuestionAnswered(
  question: Question,
  answers: Record<string, number>,
): boolean {
  if (question.layout === "instructions") return true;
  if (question.layout === "likert") {
    return question.key in answers || question.attributes.answer_key in answers;
  }
  if (question.layout === "multiple_choice") {
    return question.attributes.choices.some((c) => {
      const key = c.attributes.answer_key;
      if (!key) return false;

      return answers[key] === Number(c.attributes.selected_value);
    });
  }
  return true;
}

export function getInitialPage(
  questions: Question[],
  answers: Record<string, number> | null,
): number {
  if (!questions.length) return 0;
  // When no answers yet, start at the first question (including instruction).
  if (!answers || Object.keys(answers).length === 0) return 0;
  const firstUnanswered = questions.findIndex(
    (q) => !isQuestionAnswered(q, answers),
  );
  if (firstUnanswered === -1) return questions.length - 1;
  return firstUnanswered;
}

/** Score for the current question only. Likert: { "Qn": value }. Multiple choice: { [choice.attributes.answer_key]: value } per selected choice. */
export function getCurrentQuestionScore(
  question: Question,
  questionIndex: number,
  answers: Record<string, number>,
): Record<string, number> {
  const key = `Q${questionIndex + 1}`;
  if (question.layout === "instructions") return {};
  if (question.layout === "likert") {
    const v = answers[question.attributes.answer_key];
    return v !== undefined ? { [key]: v } : {};
  }
  if (question.layout === "multiple_choice") {
    const score: Record<string, number> = {};
    for (const c of question.attributes.choices ?? []) {
      const answerKey = c.attributes.answer_key;
      const v = answers[answerKey];
      if (v !== undefined) score[answerKey] = v;
    }
    return score;
  }
  return {};
}

export function getCurrentQuestionOrdinal(
  questions: Question[],
  currentPageIndex: number,
): number {
  if (
    !questions.length ||
    currentPageIndex < 0 ||
    currentPageIndex >= questions.length
  ) {
    return 0;
  }
  let ordinal = 0;
  for (let i = 0; i <= currentPageIndex; i++) {
    if (questions[i].layout !== "instructions") ordinal++;
  }
  return ordinal;
}
